import React, { useState } from 'react';
import { ChevronRight, Copy, Plus, Trash2 } from 'lucide-react';

const BreadcrumbGeneratorTool = () => {
  const [items, setItems] = useState([
    { name: 'Home', url: '/' },
    { name: '', url: '' }
  ]);
  const [format, setFormat] = useState('jsonld'); // jsonld, microdata, rdfa
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);

  const addItem = () => {
    setItems([...items, { name: '', url: '' }]);
  };

  const removeItem = (index) => {
    if (items.length > 2) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const generateBreadcrumbs = () => {
    const validItems = items.filter(item => item.name.trim());
    
    if (validItems.length === 0) {
      setOutput('');
      return;
    }

    let result = '';

    switch (format) {
      case 'jsonld':
        const jsonLd = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": validItems.map((item, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": item.name,
            "item": item.url || undefined
          }))
        };
        result = `<script type="application/ld+json">
${JSON.stringify(jsonLd, null, 2)}
</script>`;
        break;

      case 'microdata':
        result = `<nav aria-label="breadcrumb">
  <ol itemscope itemtype="https://schema.org/BreadcrumbList">
${validItems.map((item, i) => `    <li itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
      <a itemprop="item" href="${item.url || '#'}">
        <span itemprop="name">${item.name}</span>
      </a>
      <meta itemprop="position" content="${i + 1}" />
    </li>`).join('\n')}
  </ol>
</nav>`;
        break;

      case 'rdfa':
        result = `<nav aria-label="breadcrumb">
  <ol vocab="https://schema.org/" typeof="BreadcrumbList">
${validItems.map((item, i) => `    <li property="itemListElement" typeof="ListItem">
      <a property="item" typeof="WebPage" href="${item.url || '#'}">
        <span property="name">${item.name}</span>
      </a>
      <meta property="position" content="${i + 1}" />
    </li>`).join('\n')}
  </ol>
</nav>`;
        break;

      case 'html':
        result = `<nav aria-label="breadcrumb">
  <ol class="breadcrumb">
${validItems.map((item, i) => i === validItems.length - 1
  ? `    <li class="breadcrumb-item active" aria-current="page">${item.name}</li>`
  : `    <li class="breadcrumb-item"><a href="${item.url || '#'}">${item.name}</a></li>`
).join('\n')}
  </ol>
</nav>`;
        break;
    }

    setOutput(result);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadSample = () => {
    setItems([
      { name: 'Home', url: '/' },
      { name: 'Products', url: '/products' },
      { name: 'Electronics', url: '/products/electronics' },
      { name: 'Smartphones', url: '/products/electronics/smartphones' }
    ]);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {['jsonld', 'microdata', 'rdfa', 'html'].map((f) => (
          <button
            key={f}
            onClick={() => setFormat(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              format === f
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {f === 'jsonld' ? 'JSON-LD' : 
             f === 'microdata' ? 'Microdata' :
             f === 'rdfa' ? 'RDFa' : 'HTML'}
          </button>
        ))}
      </div>

      {/* Preview */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="text-sm text-gray-400 mb-2">Preview</div>
        <div className="flex items-center flex-wrap gap-1">
          {items.filter(i => i.name).map((item, i, arr) => (
            <React.Fragment key={i}>
              <span className={i === arr.length - 1 ? 'text-gray-400' : 'text-blue-400'}>
                {item.name}
              </span>
              {i < arr.length - 1 && <ChevronRight size={16} className="text-gray-600" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Items */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <label className="text-sm font-medium text-gray-300">Breadcrumb Items</label>
          <button onClick={loadSample} className="text-xs text-blue-400 hover:text-blue-300">
            Load Sample
          </button>
        </div>
        {items.map((item, i) => (
          <div key={i} className="flex gap-2 items-center">
            <span className="text-gray-500 text-sm w-6">{i + 1}.</span>
            <input
              type="text"
              value={item.name}
              onChange={(e) => updateItem(i, 'name', e.target.value)}
              placeholder="Page Name"
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-blue-500"
            />
            <input
              type="text"
              value={item.url}
              onChange={(e) => updateItem(i, 'url', e.target.value)}
              placeholder="/page-url"
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-blue-500"
            />
            <button
              onClick={() => removeItem(i)}
              disabled={items.length <= 2}
              className="p-2 text-red-400 hover:bg-red-900/30 disabled:text-gray-600 disabled:hover:bg-transparent rounded-lg transition-colors"
            >
              <Trash2 size={18} />
            </button>
          </div>
        ))}
        <button
          onClick={addItem}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
        >
          <Plus size={16} />
          Add Item
        </button>
      </div>

      <button
        onClick={generateBreadcrumbs}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
      >
        Generate Code
      </button>

      {output && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Generated Code</label>
          <pre className="w-full bg-gray-800 border border-gray-700 rounded-lg p-4 text-green-400 font-mono text-sm overflow-auto max-h-64">
            {output}
          </pre>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyToClipboard}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy Code'}
        </button>
        <button
          onClick={() => { setItems([{ name: 'Home', url: '/' }, { name: '', url: '' }]); setOutput(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear
        </button>
      </div>

      <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
        <h3 className="text-sm font-medium text-blue-300 mb-2">SEO Benefits</h3>
        <ul className="text-sm text-gray-400 space-y-1">
          <li>• Helps Google understand your site structure</li>
          <li>• Can appear in search results as rich snippets</li>
          <li>• Improves user navigation and experience</li>
          <li>• JSON-LD is recommended by Google</li>
        </ul>
      </div>
    </div>
  );
};

export default BreadcrumbGeneratorTool;
